import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, BookOpen, Check } from "lucide-react";
import api, { apiError } from "../lib/api";
import { BrandMark } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const STATUS = { ready: "bg-emerald-500/15 text-emerald-400", processing: "bg-amber-500/15 text-amber-400", error: "bg-red-500/15 text-red-400", none: "bg-zinc-700/60 text-zinc-300" };

export default function MachineTypes() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const canManage = ["manager", "office", "super_admin"].includes(user?.role);
  useEffect(() => { if (user && !canManage) nav("/profile", { replace: true }); }, [user, canManage, nav]);

  const load = () => api.get("/machine-types").then(({ data }) => setTypes(data)).catch(() => setTypes([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/machine-types", { name: name.trim() }); setName(""); toast.success(t("Type created")); load(); }
    catch (e) { toast.error(e.response?.status === 409 ? t("A type with that name already exists.") : apiError(e)); }
  };
  const rename = async (id) => {
    if (!editName.trim()) return;
    try { await api.put(`/machine-types/${id}`, { name: editName.trim() }); setEditId(null); toast.success(t("Type renamed")); load(); }
    catch (e) { toast.error(e.response?.status === 409 ? t("A type with that name already exists.") : apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("Delete this machine type and its parts book?"))) return;
    try { await api.delete(`/machine-types/${id}`); toast.success(t("Type deleted")); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto sm:max-w-2xl md:max-w-4xl border-x border-zinc-800/40 pb-10">
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
        <button onClick={() => nav("/profile")} className="flex items-center gap-2 text-zinc-300" data-testid="mt-back-btn"><ArrowLeft size={20} /> {t("Profile")}</button>
        <BrandMark />
      </div>
      <div className="px-4 pt-4">
        <h1 className="text-2xl font-extrabold text-white">{t("Machine Types")}</h1>
        <p className="text-zinc-400 text-sm">{t("Manage machine types & their parts books")}</p>

        <div className="flex gap-2 mt-4">
          <input className="flex-1 bg-[#1c1c1f] border border-zinc-800 text-white rounded-xl px-3 py-2.5 focus:border-[#F26722] focus:outline-none" placeholder={t("Type name")} value={name} onChange={(e) => setName(e.target.value)} data-testid="mt-name-input" />
          <button onClick={create} className="bg-[#F26722] text-white rounded-xl px-4 flex items-center gap-1 font-bold" data-testid="mt-add-btn"><Plus size={18} /> {t("Add")}</button>
        </div>

        <div className="mt-4 space-y-2">
          {loading && <p className="text-zinc-500 text-center py-8">{t("Loading...")}</p>}
          {!loading && types.length === 0 && <p className="text-zinc-500 text-center py-8" data-testid="mt-empty">{t("No machine types yet.")}</p>}
          {types.map((mt) => (
            <div key={mt.id} className="bg-[#18181B] border border-zinc-800 rounded-xl p-3.5" data-testid={`mt-row-${mt.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  {editId === mt.id ? (
                    <div className="flex gap-2 items-center">
                      <input className="bg-[#1c1c1f] border border-zinc-700 text-white rounded-lg px-2 py-1 text-sm" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`mt-edit-input-${mt.id}`} />
                      <button onClick={() => rename(mt.id)} className="text-emerald-400" data-testid={`mt-save-${mt.id}`}><Check size={18} /></button>
                    </div>
                  ) : (
                    <p className="font-bold text-white flex items-center gap-2">{mt.name} {mt.is_default && <span className="text-[10px] font-bold bg-zinc-700/60 text-zinc-300 px-2 py-0.5 rounded-full">{t("Default")}</span>}</p>
                  )}
                  <p className="text-xs text-zinc-500 mt-1 flex items-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold mr-2 ${STATUS[mt.status] || STATUS.none}`}>{mt.status}</span>
                    {mt.assemblies != null ? `${mt.assemblies} ${t("assemblies")} · ${mt.parts} ${t("parts")}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => nav(`/parts?type=${mt.id}`)} className="text-[#F26722] text-xs font-semibold flex items-center gap-1 px-2 py-1.5" data-testid={`mt-manage-${mt.id}`}><BookOpen size={15} /> {t("Manage book")}</button>
                  <button onClick={() => { setEditId(mt.id); setEditName(mt.name); }} className="text-zinc-400 p-1.5" data-testid={`mt-rename-${mt.id}`}><Pencil size={16} /></button>
                  {!mt.is_default && <button onClick={() => del(mt.id)} className="text-red-400 p-1.5" data-testid={`mt-delete-${mt.id}`}><Trash2 size={16} /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
